import BreadCrumb from "Components/Common/BreadCrumb";
import { useFormik } from "formik";
import {
  adminGetCompanyDetail,
  adminUpdateCompany,
} from "helpers/backend_helpers";
import { handleImageError } from "helpers/globalFunction";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Spinner,
  Form,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { SketchPicker } from "react-color";

const CompanyProperties = () => {
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);

  const getData = () => {
    setLoading(true);
    adminGetCompanyDetail()
      .then((response: any) => {
        setData(response);
      })
      .catch((error) => {
        toast.error("Gagal memuat data perusahaan!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      color: data?.color || "#000000",
      logo: data?.logo || "",
    },
    validationSchema: Yup.object({
      color: Yup.string().required("Warna harus diisi!"),
      logo: Yup.string(),
    }),
    onSubmit: (values) => {
      setSubmitting(true);
      const formData = new FormData();

      formData.append("color", values.color);

      if (imageFile) {
        formData.append("logo", imageFile);
      }

      adminUpdateCompany(formData)
        .then((response: any) => {
          getData();
          toast.success(
            <div>
              Data properties perusahaan <b>{data?.name}</b> berhasil diubah!
            </div>
          );
          setSelectedImage(null);
          setImageFile(null);
        })
        .catch((error) => {
          toast.error("Gagal mengubah data perusahaan!");
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5MB!");
      return;
    }

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Format file harus PNG, JPG, atau GIF!");
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onload = (e: any) => {
      setSelectedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleColorChange = (color: any) => {
    validation.setFieldValue("color", color.hex);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImageFile(null);
  };

  const handleReset = () => {
    validation.resetForm();
    setSelectedImage(null);
    setImageFile(null);
    setShowColorPicker(false);
  };

  useEffect(() => {
    getData();
  }, []);

  document.title = "Properties Perusahaan";

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Spinner color="primary" size="lg" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Properties Perusahaan"
            pageTitle="Properties Perusahaan"
          />

          <Form onSubmit={validation.handleSubmit}>
            <Row className="mt-4">
              <Col xxl={4} lg={5}>
                <Card className="shadow-sm">
                  <CardBody className="p-4">
                    <div className="mb-3">
                      <h5 className="card-title mb-3">
                        {/* <i className="ri-image-line me-2"></i> */}
                        Logo Perusahaan
                      </h5>
                      <p className="text-muted small mb-4">
                        Upload logo perusahaan Anda. Format: PNG, JPG, GIF (Max:
                        5MB)
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                        <img
                          src={selectedImage || data?.logo}
                          className="rounded-circle avatar-xxl img-thumbnail user-profile-image shadow"
                          alt="logo"
                          onError={handleImageError}
                        />
                        <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                          <Input
                            id="profile-img-file-input"
                            type="file"
                            className="profile-img-file-input"
                            accept="image/png, image/gif, image/jpeg, image/jpg"
                            onChange={handleImageChange}
                          />
                          <Label
                            htmlFor="profile-img-file-input"
                            className="profile-photo-edit avatar-xs"
                          >
                            <div className="avatar-xs cursor-pointer">
                              <span className="avatar-title rounded-circle bg-light text-body shadow">
                                <i className="ri-camera-fill"></i>
                              </span>
                            </div>
                          </Label>
                        </div>
                      </div>

                      {selectedImage && (
                        <div className="mb-3">
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={handleRemoveImage}
                          >
                            <i className="ri-delete-bin-line me-1"></i>
                            Hapus Preview
                          </Button>
                        </div>
                      )}

                      <div className="border-top pt-4 mt-4">
                        <h6 className="fs-16 mb-2">{data?.name}</h6>
                        <p className="text-muted mb-2">{data?.initial}</p>
                        {data?.address && (
                          <div className="text-muted small">
                            <i className="ri-map-pin-line me-1"></i>
                            {data?.address}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xxl={8} lg={7}>
                <Card className="shadow-sm">
                  <CardBody className="p-4">
                    <div className="mb-4">
                      <h5 className="card-title mb-3">
                        {/* <i className="ri-palette-line me-2"></i> */}
                        Warna Tema Perusahaan
                      </h5>
                      <p className="text-muted small mb-0">
                        Pilih warna tema utama untuk brand perusahaan Anda
                      </p>
                    </div>

                    <Row className="align-items-center">
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">
                            Warna yang Dipilih
                          </Label>
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="border rounded shadow-sm cursor-pointer"
                              style={{
                                width: "100px",
                                height: "100px",
                                backgroundColor: validation.values.color,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setShowColorPicker(!showColorPicker)
                              }
                            />
                            <div>
                              <Input
                                type="text"
                                className="form-control"
                                value={validation.values.color}
                                onChange={(e) =>
                                  validation.setFieldValue(
                                    "color",
                                    e.target.value
                                  )
                                }
                                invalid={
                                  validation.touched.color &&
                                  validation.errors.color
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.color &&
                              validation.errors.color ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.color}
                                </FormFeedback>
                              ) : null}
                              <small className="text-muted">
                                Klik kotak untuk memilih warna
                              </small>
                            </div>
                          </div>
                        </div>

                        {showColorPicker && (
                          <div className="mb-3">
                            <SketchPicker
                              color={validation.values.color}
                              onChange={handleColorChange}
                              width="250px"
                            />
                          </div>
                        )}
                      </Col>

                      <Col md={6}>
                        <div className="p-4 rounded border bg-light">
                          <h6 className="mb-3">Preview Warna</h6>
                          <div className="d-flex flex-column gap-2">
                            {/* <Button
                              type="button"
                              style={{
                                backgroundColor: validation.values.color,
                                borderColor: validation.values.color,
                              }}
                            >
                              Contoh Button
                            </Button> */}
                            <div
                              className="p-3 rounded text-white"
                              style={{
                                backgroundColor: validation.values.color,
                              }}
                            >
                              <strong>Contoh Header</strong>
                              <p className="mb-0 small">
                                Ini adalah preview penggunaan warna tema
                              </p>
                            </div>
                            {/* <div
                              className="border-start border-4 p-2"
                              style={{
                                borderColor:
                                  validation.values.color + " !important",
                              }}
                            >
                              <small className="text-muted">
                                Contoh Accent Border
                              </small>
                            </div> */}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-4 pt-4 border-top">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          size="sm"
                          type="button"
                          color="light"
                          onClick={handleReset}
                          disabled={submitting}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          type="submit"
                          color="success"
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Menyimpan...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line me-1"></i>
                              Simpan Perubahan
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card className="shadow-sm bg-soft-info border-0">
                  <CardBody className="p-4">
                    <div className="d-flex">
                      <div className="flex-shrink-0">
                        <i className="ri-information-line fs-20 text-info"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-2">Tips Pemilihan Warna & Logo</h6>
                        <ul className="mb-0 small text-muted">
                          {/* <li>
                            Pilih warna yang konsisten dengan brand identity
                            perusahaan
                          </li> */}
                          <li>
                            Gunakan logo dengan resolusi tinggi untuk hasil
                            terbaik
                          </li>
                          <li>
                            Pastikan warna memiliki kontras yang baik untuk
                            keterbacaan
                          </li>
                          <li>
                            Perubahan akan diterapkan ke seluruh sistem Aplikasi
                            Karyawan
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompanyProperties;
